/**
 * Item Exit Status and Readiness Types
 *
 * The canonical item state model has four orthogonal dimensions:
 *
 * 1. exit_status (stored) — Is the item in active consideration?
 *    active | deferred | archived
 *
 * 2. Readiness (computed from named inputs) — Can this item be used for the
 *    intended action in the current context?
 *    Derived from: title, dedupe status, required attributes, accepted values.
 *    Never inferred from exit_status.
 *
 * 3. Cycle participation (on cycle_item_participation) — What is this item's
 *    status within a specific cycle?
 *    nominated | confirmed | shaped_for_cycle | ranked | excluded
 *
 * 4. Classification State (stored) — Has this item's category and altitude
 *    been determined, and by whom?
 *    empty | proposed | approved
 *    Tracks the provenance and acceptance lifecycle of comparability-sensitive
 *    attributes (category, altitude). See itemClassification.ts for the full type.
 *
 * Triage outcome (accept/defer/reject) maps directly to exit_status.
 * There are no intake sub-statuses. "Shaping" is a deprecated term.
 */

// Re-export ClassificationStatus so consumers can import from one place.
export type { ClassificationStatus } from './itemClassification';

// ============================================================================
// Exit Status
// ============================================================================

/**
 * The canonical stored state on an Item.
 * Answers: "Is this item in active consideration?"
 *
 * - active   — in active consideration (default)
 * - deferred — explicitly postponed; not eligible for automatic session inclusion
 * - archived — removed from active consideration; retained for history; restorable
 */
export type ItemExitStatus = 'active' | 'deferred' | 'archived';

export const EXIT_STATUS_LABELS: Record<ItemExitStatus, string> = {
  active: 'Active',
  deferred: 'Deferred',
  archived: 'Archived',
};

export const EXIT_STATUS_DESCRIPTIONS: Record<ItemExitStatus, string> = {
  active: 'In active consideration',
  deferred: 'Explicitly postponed; retained for future cycles',
  archived: 'Removed from active consideration; kept for history',
};

// ============================================================================
// Planning Maturity
// ============================================================================

/**
 * Where an item sits on the Directional → Detailed planning continuum.
 *
 * Directional Planning is planning in service of prioritization.
 * Detailed Planning is planning in service of execution.
 *
 * An item should reach 'detailed_plan_exists' only after its priority
 * has been accepted or sufficiently committed.
 */
export type PlanningMaturity =
  | 'directional'           // Rough signals only; enough to compare responsibly
  | 'partially_analyzed'    // Key constraints identified; some analysis done
  | 'ready_for_detailed'    // Priority accepted; Detailed Planning can begin
  | 'detailed_plan_exists'; // Full execution plan in place

export const PLANNING_MATURITY_LABELS: Record<PlanningMaturity, string> = {
  directional: 'Directional',
  partially_analyzed: 'Partially Analyzed',
  ready_for_detailed: 'Ready for Detailed Planning',
  detailed_plan_exists: 'Detailed Plan Exists',
};

export const PLANNING_MATURITY_DESCRIPTIONS: Record<PlanningMaturity, string> = {
  directional: 'Rough signals only — enough to compare responsibly. Detailed Planning not yet warranted.',
  partially_analyzed: 'Key constraints identified. Some analysis done. Not yet ready for full execution planning.',
  ready_for_detailed: 'Priority accepted or sufficiently committed. Detailed Planning can begin.',
  detailed_plan_exists: 'Full execution plan in place. Staffing, sequencing, and delivery commitments established.',
};

export const PLANNING_MATURITY_ORDER: PlanningMaturity[] = [
  'directional',
  'partially_analyzed',
  'ready_for_detailed',
  'detailed_plan_exists',
];

// ============================================================================
// Uncertainty Encoding
// ============================================================================

/**
 * Epistemic status of the core facts behind an item.
 * Used in Directional Planning to prevent unvalidated assumptions from being
 * treated as confirmed facts during Priority Formation.
 *
 * "Directional inputs should inform comparison without pretending to be
 * execution commitments." — Platform Formation Handoff §7
 */
export type ItemKnowledgeStatus =
  | 'known'      // Confirmed facts; high confidence in the basis
  | 'estimated'  // Reasoned guess with some evidence
  | 'assumed'    // Taken as true without validation
  | 'inferred'   // Derived from indirect signals
  | 'disputed'   // Active disagreement about the facts
  | 'unknown';   // Basis not yet assessed

export const KNOWLEDGE_STATUS_LABELS: Record<ItemKnowledgeStatus, string> = {
  known: 'Known',
  estimated: 'Estimated',
  assumed: 'Assumed',
  inferred: 'Inferred',
  disputed: 'Disputed',
  unknown: 'Unknown',
};

export const KNOWLEDGE_STATUS_DESCRIPTIONS: Record<ItemKnowledgeStatus, string> = {
  known: 'Core facts are confirmed with evidence.',
  estimated: 'Reasoned guess with some supporting evidence.',
  assumed: 'Taken as true without validation — should be verified before committing.',
  inferred: 'Derived from indirect signals rather than direct evidence.',
  disputed: 'Active disagreement about the underlying facts.',
  unknown: 'Basis has not yet been assessed.',
};

export const KNOWLEDGE_STATUS_ORDER: ItemKnowledgeStatus[] = [
  'known', 'estimated', 'assumed', 'inferred', 'disputed', 'unknown',
];

/**
 * Overall confidence in an item's Directional Planning inputs.
 * Separate from session/tool confidence values (which measure ranking certainty).
 * This measures the epistemic quality of the item's planning basis.
 */
export type ItemConfidenceLevel = 'low' | 'medium' | 'high';

export const CONFIDENCE_LEVEL_LABELS: Record<ItemConfidenceLevel, string> = {
  low: 'Low confidence',
  medium: 'Medium confidence',
  high: 'High confidence',
};

export const CONFIDENCE_LEVEL_DESCRIPTIONS: Record<ItemConfidenceLevel, string> = {
  low: 'Rough signal — significant uncertainty in estimates and assumptions.',
  medium: 'Reasonable basis — some assumptions remain unvalidated.',
  high: 'Well-evidenced — assumptions are mostly validated.',
};

export const CONFIDENCE_LEVEL_ORDER: ItemConfidenceLevel[] = ['low', 'medium', 'high'];

// ============================================================================
// Readiness
// ============================================================================

/**
 * Named readiness inputs — the specific conditions an item must satisfy
 * to be used for a given action.
 *
 * Readiness is contextual: which inputs are required depends on what action
 * is being attempted (e.g., inclusion in a session vs. comparison ranking).
 *
 * The canon calls these "Missing Inputs" when they are absent.
 */
export type ReadinessInput =
  | 'title'               // Item must have a non-empty title
  | 'dedupe_resolved'     // Duplicate status must be decided (unique / merged / duplicate_of)
  | 'category_assigned'   // Item category (problem / opportunity / etc.) must be set
  | 'altitude_set'        // Altitude level (1–5) or parent relationship must exist
  | 'required_attributes' // All required attributes for the session's criteria must have accepted values
  | 'accepted_values';    // At least one accepted value exists

/**
 * Readiness check result for a single named input.
 */
export interface ReadinessCheck {
  input: ReadinessInput;
  label: string;
  satisfied: boolean;
  /** Human-readable description of what is missing when not satisfied. */
  missingLabel?: string;
  /** Where the user can go to resolve this. */
  remediationPath?: string;
}

/**
 * Aggregate readiness result for an item in a given context.
 * Computed — never stored.
 */
export interface ItemReadinessResult {
  isReady: boolean;
  checks: ReadinessCheck[];
  /** Inputs that are required and not satisfied. */
  blockers: ReadinessCheck[];
  /** Inputs that are recommended but not required. */
  warnings: ReadinessCheck[];
}

// ============================================================================
// Defer and Archive Metadata
// ============================================================================

export interface DeferMetadata {
  deferredAt: Date;
  deferredBy: string;
  deferReason?: string;
  deferReviewDate?: Date;
}

// ============================================================================
// Value Provenance Types (unchanged — these are about attribute values, not item state)
// ============================================================================

export type ProposalStatus = 'proposed' | 'accepted' | 'rejected' | 'withdrawn';

/**
 * Differentiates how a value input should be interpreted:
 * - 'estimate': soft, uncertain input expected from many contributors; aggregated into a consensus.
 * - 'score': authoritative, definitive assessment; maps to the acceptance/lock flow.
 */
export type ValueKind = 'estimate' | 'score';

/**
 * How multiple estimate proposals are combined into a single accepted value.
 */
export type ValueAggregationMethod =
  | 'mean'
  | 'median'
  | 'mode'
  | 'unanimous'
  | 'facilitator_override';

/**
 * A single contributor's raw input captured when aggregating multiple proposals
 * into one AcceptedValue. Stored as JSONB in accepted_values.aggregation_inputs.
 */
export interface AggregatedValueInput {
  proposalId: string;
  proposedBy: string;
  value: any;
  confidence?: number;
}

export interface ValueProposal {
  id: string;
  itemId: string;
  attributeKey: string;
  /** Which measurement scale this proposal applies to (multi-scale attributes). */
  scaleId?: string | null;
  proposedValue: any;
  proposedBy: string;
  proposedByName?: string;
  proposedAt: Date;
  proposerRole: string;
  rationale?: string;
  evidence?: string;
  confidence?: number;
  /** Whether this proposal is an estimate (soft, uncertain) or a score (authoritative). Defaults to 'score'. */
  valueKind?: ValueKind;
  status: ProposalStatus;
  reviewedBy?: string;
  reviewedAt?: Date;
  reviewNote?: string;
  trackId?: string;
  cycleId?: string;
  /** Links this proposal to a structured InputRound, if any. */
  roundId?: string | null;
  /**
   * The session this contribution was made in (value_proposals.session_id).
   * A contribution from a session is still offered up: an approver can accept
   * it as the workspace's official value from the item page.
   */
  sessionId?: string | null;
  /** Where the contribution came from (value_proposals.source; 'user' by default, 'session' from session entry). */
  source?: string | null;
}

export type AcceptanceMethod =
  | 'consensus'
  | 'vote'
  | 'authority'
  | 'facilitator_override'
  /** Migration 919: an agent's value accepted by a standing rule, in the name of the person who set the rule. */
  | 'standing_rule';

export interface AcceptedValue {
  id: string;
  itemId: string;
  attributeKey: string;
  scaleId?: string | null;
  value: any;
  acceptedFromProposalId?: string;
  acceptedBy: string;
  acceptedAt: Date;
  acceptanceMethod: AcceptanceMethod;
  acceptanceNote?: string;
  sessionScope?: string;
  lockedAt?: Date;
  lockedBy?: string;
  /** Whether this accepted value represents an estimate consensus or a definitive score. */
  valueKind?: ValueKind;
  /** Number of contributors whose proposals were aggregated into this value. */
  contributorCount?: number;
  /** The method used to aggregate multiple proposals into this value. */
  aggregationMethod?: ValueAggregationMethod;
  /** Raw per-contributor inputs captured at aggregation time. */
  aggregationInputs?: AggregatedValueInput[];
  /** max - min of raw numeric inputs (undefined for categorical). */
  spread?: number;
  /** Population standard deviation of raw numeric inputs (undefined for categorical). */
  stdDev?: number;
  /** True when std_dev > 1.0 — mirrors the DB generated column. */
  isContested?: boolean;
}

export type ExceptionScope = 'global' | 'session';

export interface AcceptedException {
  id: string;
  itemId: string;
  attributeKey: string;
  reason: string;
  scope: ExceptionScope;
  sessionId?: string;
  acceptedBy: string;
  acceptedAt: Date;
  riskNote?: string;
}

// ============================================================================
// Altitude Levels (unchanged)
// ============================================================================

export type AltitudeLevel = 1 | 2 | 3 | 4 | 5;

export const ALTITUDE_LEVEL_NAMES: Record<AltitudeLevel, string> = {
  5: 'Strategic',
  4: 'Program',
  3: 'Capability',
  2: 'Delivery',
  1: 'Tactical',
};

export const ALTITUDE_LEVEL_DESCRIPTIONS: Record<AltitudeLevel, string> = {
  5: 'Near-irreversible, org-wide coordination, company-wide scope',
  4: 'Hard to undo, cross-functional alignment, multi-product scope',
  3: 'Moderate lock-in, cross-team coordination, multi-workflow scope',
  2: 'Low switching cost, one or two teams, single workflow scope',
  1: 'Easily reversed, one person or team, isolated scope',
};

export const ALTITUDE_LEVELS_ORDERED: AltitudeLevel[] = [5, 4, 3, 2, 1];

// ============================================================================
// Category-Specific Altitude Names
// ============================================================================

/**
 * Altitude names per item category — the canonical track cell terminology.
 *
 * Each name is the identity of a (category × altitude) track cell.
 * Altitude is derived from three structural dimensions:
 *   Reversibility: how locked in are we if we're wrong?
 *   Coordination complexity: how many groups must align?
 *   Scope / blast radius: how broadly does this reach?
 *
 * Level 5 = near-irreversible, org-wide coordination, company-wide scope
 * Level 4 = hard to undo, cross-functional alignment, multi-product scope
 * Level 3 = moderate lock-in, cross-team coordination, multi-workflow scope
 * Level 2 = low switching cost, one or two teams, single workflow scope
 * Level 1 = easily reversed, one person/team, isolated scope
 */
export const ALTITUDE_NAMES_BY_CATEGORY: Record<string, Record<AltitudeLevel, string>> = {
  problem: {
    5: 'Root Cause',
    4: 'Pattern',
    3: 'Gap',
    2: 'Defect',
    1: 'Bug',
  },
  opportunity: {
    5: 'Strategy',
    4: 'Program',
    3: 'Initiative',
    2: 'Prospect',
    1: 'Quick Win',
  },
  risk: {
    5: 'Crisis',
    4: 'Threat',
    3: 'Exposure',
    2: 'Concern',
    1: 'Flag',
  },
  solution: {
    5: 'Platform',
    4: 'Initiative',
    3: 'Epic',
    2: 'Story',
    1: 'Task',
  },
  commitment: {
    5: 'Mandate',
    4: 'Pledge',
    3: 'Contract',
    2: 'Deliverable',
    1: 'Action',
  },
  investment: {
    5: 'Transformation',
    4: 'Portfolio',
    3: 'Project',
    2: 'Pilot',
    1: 'Experiment',
  },
};

/**
 * Returns the altitude name for a given level and optional item category.
 * Falls back to generic ALTITUDE_LEVEL_NAMES when category is unknown or absent.
 */
export function getAltitudeName(
  level: AltitudeLevel | number,
  category?: string | null,
): string {
  const lvl = level as AltitudeLevel;
  if (category && ALTITUDE_NAMES_BY_CATEGORY[category]) {
    return ALTITUDE_NAMES_BY_CATEGORY[category][lvl] ?? ALTITUDE_LEVEL_NAMES[lvl] ?? `Level ${level}`;
  }
  return ALTITUDE_LEVEL_NAMES[lvl] ?? `Level ${level}`;
}

// ============================================================================
// Legacy Altitude Labels (Deprecated)
// ============================================================================

/** @deprecated Use AltitudeLevel instead. */
export type ItemAltitude =
  | 'outcome'
  | 'initiative'
  | 'epic'
  | 'feature'
  | 'story'
  | 'task';

/** @deprecated Use ALTITUDE_LEVELS_ORDERED instead. */
export const ALTITUDE_ORDER: ItemAltitude[] = [
  'outcome', 'initiative', 'epic', 'feature', 'story', 'task',
];

export const LEGACY_ALTITUDE_TO_LEVEL: Record<ItemAltitude, AltitudeLevel> = {
  'outcome': 5,
  'initiative': 5,
  'epic': 4,
  'feature': 3,
  'story': 2,
  'task': 1,
};

export interface ItemHierarchy {
  altitude: ItemAltitude;
  parentItemId?: string;
  childItemIds: string[];
}

// ============================================================================
// Dedupe Status (unchanged)
// ============================================================================

export type DedupeStatus =
  | 'pending'
  | 'unique'
  | 'merged'
  | 'duplicate_of'
  | 'split';

// ============================================================================
// Deprecated: ItemDetailedState
//
// The 12-value detailed state enum has been replaced by:
//   - exit_status (ItemExitStatus) for stored item state
//   - computed readiness (ItemReadinessResult) for eligibility
//   - CycleItemStatus for cycle participation
//
// These aliases exist only to keep legacy code compiling during the sweep.
// Remove all usages and delete this section when the consumer sweep is done.
// ============================================================================

/**
 * @deprecated Use ItemExitStatus. Legacy 12-value enum — being removed.
 */
export type ItemDetailedState =
  | 'RAW'
  | 'TRIAGED'
  | 'DIRECT'
  | 'SHAPING'
  | 'SHAPED'
  | 'VALUES_PROPOSED'
  | 'RECONCILED'
  | 'PRIORITIZED'
  | 'FOCUS'
  | 'LOCKED'
  | 'DEFERRED'
  | 'ARCHIVED';

/**
 * @deprecated Map a legacy ItemDetailedState to the new ItemExitStatus.
 */
export function legacyStateToExitStatus(state: ItemDetailedState | undefined): ItemExitStatus {
  if (state === 'DEFERRED') return 'deferred';
  if (state === 'ARCHIVED') return 'archived';
  return 'active';
}

/**
 * @deprecated Use item.exit_status.
 */
export function getDetailedState(item: { exit_status?: ItemExitStatus; detailedState?: ItemDetailedState }): ItemDetailedState {
  if (item.detailedState) return item.detailedState;
  if (item.exit_status === 'deferred') return 'DEFERRED';
  if (item.exit_status === 'archived') return 'ARCHIVED';
  return 'RAW';
}

/**
 * @deprecated Use Item from lifecycle.ts. Kept for compilation during consumer sweep.
 * CandidateWithLifecycle was the old type combining Item + ItemDetailedState fields.
 */
export type CandidateWithLifecycle = Record<string, any>;
/** @deprecated Use Item. */
export type ItemWithLifecycle = Record<string, any>;
/** @deprecated */
export function hasLifecycleExtension(_item: any): boolean { return false; }
/** @deprecated */
export const STATE_INVARIANTS: Record<string, string[]> = {};
/** @deprecated */
export const STATE_BANNER_CONFIGS: Record<string, any> = {};

export type ReadinessType = 'structural' | 'value' | 'agreement' | 'session' | 'execution';

export interface ReadinessIndicator {
  type: ReadinessType;
  label: string;
  current: number;
  total: number;
  tooltip: string;
}

// ============================================================================
// Transition audit types (kept — exit-status transitions are still audited)
// ============================================================================

export interface StateTransitionAuditEvent {
  id: string;
  itemId: string;
  fromStatus: ItemExitStatus | null;
  toStatus: ItemExitStatus;
  action: string;
  actorId: string;
  actorRole: string;
  timestamp: Date;
  reason?: string;
  metadata?: Record<string, unknown>;
}

/** @deprecated Alias for StateTransitionAuditEvent during sweep. */
export type { StateTransitionAuditEvent as StateTransition };
export interface TransitionValidationResult {
  allowed: boolean;
  reason?: string;
  missingGuards?: string[];
}
